import { createGlobalState, useEventListener } from '@vueuse/core';
import { ref } from 'vue';
import { getMeetingDetail } from '@/services/MeetingSchedulerService';
import { MeetingDetail, TimeSlotDetail } from '@/models/MeetingScheduler';

// Global so the flash/popover state is shared across every MeetingSlotConflict (only one popover
// open at a time) and can still be closed centrally from MeetingDetail on meeting change.
const useConflictPopover = createGlobalState(() => {
  const openSlotId = ref<string | null>(null);
  const conflictMeetings = ref<MeetingDetail[]>([]);
  const isLoading = ref<boolean>(false);

  function close() {
    openSlotId.value = null;
    conflictMeetings.value = [];
    isLoading.value = false;
  }

  async function open(slot: TimeSlotDetail) {
    if (!slot.conflicts?.length) return;
    openSlotId.value = slot.timeSlotId;
    conflictMeetings.value = [];
    isLoading.value = true;
    try {
      const meetings = await Promise.all(
        (slot.conflicts ?? []).map((conflict) => getMeetingDetail(conflict.meetingId))
      );
      // Guard against a stale response when the user already switched to another slot.
      if (openSlotId.value === slot.timeSlotId) conflictMeetings.value = meetings;
    } catch {
      conflictMeetings.value = [];
    } finally {
      if (openSlotId.value === slot.timeSlotId) isLoading.value = false;
    }
  }

  return {
    openSlotId, conflictMeetings, isLoading, open, close
  };
});

export function useConflictDismiss(): void {
  const { openSlotId, close } = useConflictPopover();
  useEventListener(() => (openSlotId.value ? document : undefined), 'click', close, { capture: true });
}

export default useConflictPopover;
